// P/L of an LP position in USD.
//
// (close − open) × units × direction is in the pair's QUOTE currency. Only pairs
// quoted in USD (EURUSD, XAUUSD, …) are already dollars. This converts the rest:
//   USD quote (XXXUSD, metals)   as it is
//   USD base  (USDXXX)           ÷ the pair's own price (mid)
//   cross     (EURGBP, …)        × GBPUSD, or ÷ USDGBP, whichever the LP quotes (mid)
// No rate → null: show "—" and leave the row out of totals. Never the raw figure.

/** Six-letter currency pair of an LP symbol: "EUR/USD", "EURUSD.r" → "EURUSD"; else null. */
export function pairOf(symbol: string): string | null {
  const s = symbol.toUpperCase().replace(/[^A-Z]/g, '');
  return s.length >= 6 ? s.slice(0, 6) : null;
}

/** Quote currency: the instrument's own field when given, else the pair's last three letters. */
export function quoteCcyOf(symbol: string, instrumentCurrency?: string): string | null {
  const c = (instrumentCurrency ?? '').trim().toUpperCase();
  if (c.length === 3) return c;
  const p = pairOf(symbol);
  return p ? p.slice(3, 6) : null;
}

/**
 * The symbol whose price converts this symbol's quote currency to USD, or null
 * when none is needed (USD quote) or the symbol is USD-based (uses its own price).
 * `has(sym)` says whether the LP quotes a symbol; the XXXUSD form is preferred.
 */
export function conversionSymbolOf(
  symbol: string,
  instrumentCurrency: string | undefined,
  has: (sym: string) => boolean,
): string | null {
  const q = quoteCcyOf(symbol, instrumentCurrency);
  if (!q || q === 'USD') return null;
  const p = pairOf(symbol);
  if (p && p.startsWith('USD')) return null;
  for (const cand of [`${q}USD`, `${q}/USD`, `USD${q}`, `USD/${q}`]) if (has(cand)) return cand;
  return null;
}

/**
 * Quote-currency P/L to USD.
 *   ownMid:  mid price of the symbol itself (used for USDXXX)
 *   convSymbol / convMid: the conversion symbol from conversionSymbolOf and its mid
 */
export function toUsd(
  quotePnl: number,
  symbol: string,
  instrumentCurrency: string | undefined,
  ownMid: number | null,
  convSymbol: string | null,
  convMid: number | null,
): number | null {
  const q = quoteCcyOf(symbol, instrumentCurrency);
  if (!q) return null;
  if (q === 'USD') return quotePnl;
  const p = pairOf(symbol);
  if (p && p.startsWith('USD')) return ownMid && ownMid > 0 ? quotePnl / ownMid : null;
  if (!convSymbol || !convMid || convMid <= 0) return null;
  const conv = pairOf(convSymbol);
  if (!conv) return null;
  return conv.startsWith('USD') ? quotePnl / convMid : quotePnl * convMid;
}
